import { h, Fragment } from 'preact';
import clsx from 'clsx';
import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'preact/hooks';
import { useThreekit } from '../../threekit-context';
import { useModal } from '../../modal-context';
import Panel from '../../../utils/panel';
import { trackAddToCart } from '../util/dataLayerEvents';
import ThreekitIcons from '../threekit-icons';
import { formatMoney } from '../../../utils/helpers';
import ErrorModal from './error-modal';
import { CartClearWarningModal } from '../../../utils/cart-clear-warning-modal';
import getLineItemAttributes from '../../../utils/storefront/get-attributes';
import closeCustomizer from '../util/closeCustomizer';

const KNIFE_CATEGORIES = {
  Handle: [
    'Handle Material',
    'Front Handle Color',
    'Back Handle Color',
    'Liner Color',
    'Bolster Color',
  ],
  Blade: [
    'Blade Steel',
    'Blade Colors',
    'Blade Design',
    'Add Back Image',
    'Add Back Pattern',
    'Add Back Text',
    'Add Front Image',
    'Add Front Pattern',
    'Add Front Text',
    'Choose Custom Art',
    'Choose Font',
    'Add Text Line 1',
    'Add Text Line 2',
    'Upload Image',
    'Stock Image',
  ],
  Hardware: [
    'Clip',
    'Thumbstude & Spacer Color',
    'Screws Color',
    'Axis Color',
    'Pivot Ring',
  ],
};

function ReviewYourKnifeModal() {
  const [customKnife, setCustomKnife] = useState(null);
  const [acknowledgedTerms, setAcknowledgedTerms] = useState(false);
  const [hasWarranty, setHasWarranty] = useState(false);
  const [hasCustomized, setHasCustomized] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [warningText, setWarningText] = useState(false);
  const [warningTitle, setWarningTitle] = useState(false);
  const submitKnifeButton = useRef(null);
  const { modalName, closeModal } = useModal();
  const {
    reviewTerms,
    reviewTermsNotSoldOnSite,
    reviewTermsSoldOnSite,
    getKnifeDataForCart,
    saveKnifeConfiguration,
    getAllVariants,
    bladeSide,
    template,
    productTag,
    productHandle,
    personalizeTextOne,
    personalizeTextTwo,
    frontCustomImage,
    frontImage,
    hasFlaggedContent,
  } = useThreekit();
  const [error, setError] = useState(false);
  const isPersonalizeProduct = productTag === 'cstm:personalization';
  const isServiceProduct = productTag === 'cstm:service';

  const modalOpen = useMemo(
    () => modalName === 'review-your-knife',
    [modalName],
  );

  const addToCart = async (items) => {
    const productId = items
      .flatMap((item) => item.attributes || [])
      .find((attr) => attr.key === '_product_id')
      ?.value?.split('/Product/')[1];

    const variantId = JSON.parse(
      items
        .find((item) =>
          item.attributes?.some((attr) => attr.key === '_product_id'),
        )
        ?.attributes?.find((attr) => attr.key === '_variant')?.value || '{}',
    )?.id?.split('/ProductVariant/')[1];

    trackAddToCart(items);
    const addToCartEvent = new CustomEvent('cart:add', {
      detail: {
        payload: items,
        ...(productId &&
          variantId && {
            trackingPayload: {
              item: productId,
              q: 1,
              v: variantId,
            },
          }),
      },
    });
    document.dispatchEvent(addToCartEvent);
  };

  useEffect(() => {
    setShowWarning(hasWarranty || hasCustomized);
  }, [hasWarranty, hasCustomized]);

  const getKnifeData = useCallback(async () => {
    const knifeData = await getKnifeDataForCart(true, true);

    if (!knifeData) {
      closeModal();
      setError(true);
      setTimeout(() => setError(false), 10000);
      return;
    }

    const mainItem = knifeData.items.find((item) =>
      item.attributes.find(
        // TODO: find a beter way to identify main product for 2d products
        (attr) =>
          (attr.key === '_is_main' && attr.value === 'true') ||
          (attr.key === '_knifeConfiguration' &&
            !(attr.key === '_is_component' && attr.value === 'true')),
      ),
    );
    const knifeConfiguration = JSON.parse(
      mainItem.attributes.find((attr) => attr.key === '_knifeConfiguration')
        ?.value,
    );
    /**
     * Adds a `variant` key to each configuration item so we can display
     * the name, value, and price of the attribute
     */
    const recordedValues = [];
    const knifeConfigurationWithVariants = knifeConfiguration
      .map((attribute) => {
        const { attributeName } = attribute;

        // Text and Image attributes have the price object.
        if (attribute.price || isServiceProduct) return attribute;

        const attributeLineItem = knifeData.items.find((item) => {
          const configuration = JSON.parse(
            item.attributes.find((attr) => attr.key === '_configuration')
              ?.value,
          );

          return !!configuration[attributeName];
        });

        const attributeVariant = attributeLineItem
          ? JSON.parse(
              attributeLineItem.attributes.find(
                (attr) => attr.key === '_variant',
              )?.value,
            )
          : {};

        /**
         * Hides duplicate values to reduce visual duplicate attributes as we
         * only show the value in the review modal, not the name
         */
        const valueExists = recordedValues.includes(attribute.value);
        recordedValues.push(attribute.value);

        /**
         * TODO: Find a way to filter out items that have the product tag `Handle_Material` without
         * the adjacent `HAndle_Color` tag
         */

        return valueExists
          ? null
          : {
              ...attribute,
              variant: attributeVariant,
              price: [
                'Blade Colors',
                `${bladeSide} Handle Color`,
                'Handle Color',
              ].includes(attributeName)
                ? 0
                : Number(attributeVariant.priceV2?.amount),
            };
      })
      .filter(Boolean);

    const categorizedKnifeConfiguration = knifeConfigurationWithVariants.reduce(
      (acc, attribute) => {
        const { attributeName } = attribute;

        const category = Object.keys(KNIFE_CATEGORIES).find((category) =>
          KNIFE_CATEGORIES[category].includes(attributeName),
        );

        if (!category) return acc;

        return {
          ...acc,
          [category]: [...(acc[category] || []), attribute],
        };
      },
      {},
    );

    const newCustomKnife = {
      ...knifeData,
      mainItem,
      imageSrc: mainItem.attributes.find(
        (attr) => attr.key === '_product_image_url',
      )?.value,
      imageSrcBack: mainItem.attributes.find(
        (attr) => attr.key === '_product_image_url_back',
      )?.value,
      categorizedKnifeConfiguration: isServiceProduct
        ? { Blade: [...knifeConfiguration] }
        : categorizedKnifeConfiguration,
    };
    setCustomKnife(newCustomKnife);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getKnifeDataForCart]);

  const handleCloseCustomizer = () => {
    if (template !== 'product.customizer') {
      closeCustomizer();
    }
  };

  const handleContinuetoCart = async () => {
    if (hasFlaggedContent) return;
    if (!acknowledgedTerms && !isPersonalizeProduct) return;
    submitKnifeButton?.current?.setAttribute('disabled', 'disabled');

    if (isServiceProduct) {
      const knifeData = await getKnifeDataForCart(true, true);
      window.serviceProductInfo = knifeData;
      const closeCustomizerEvent = new Event('closeCustomizer');
      document.dispatchEvent(closeCustomizerEvent);
      document.body.classList.remove('overflow-hidden');
      submitKnifeButton.current.removeAttribute('disabled');
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('configMode')) {
      const { subtotal } = getAllVariants();
      const savedKnife = await saveKnifeConfiguration();
      // eslint-disable-next-line no-alert
      alert(
        `Threekit ID: ${savedKnife.id}, Subtotal: ${subtotal}, Image: ${savedKnife.thumbnail}`,
      );
      submitKnifeButton.current.removeAttribute('disabled');
      return;
    }

    const hasWarranty = window.GlobalCartContext?.state?.lines?.some((line) =>
      line.merchandise?.product?.handle?.startsWith('service-request-product'),
    );

    if (hasWarranty) {
      setHasWarranty(true);
      submitKnifeButton.current.removeAttribute('disabled');
      return;
    }

    if (
      (personalizeTextOne === '' || personalizeTextOne === undefined) &&
      (personalizeTextTwo === '' || personalizeTextTwo === undefined) &&
      !frontCustomImage &&
      frontImage === '' &&
      isPersonalizeProduct
    ) {
      setWarningTitle('No custom changes made!');
      setWarningText(
        `When clicking the “continue” button, you will be adding the product to your cart without any customizations made. Are you sure you want to proceed?`,
      );
      setHasCustomized(true);
      submitKnifeButton.current.removeAttribute('disabled');
      return;
    }

    if (searchParams.has('cartUpdate')) {
      const cart = window.GlobalCartContext;
      // find the product that matches the handle of the current product in the cart
      const productInCart = cart.state.lines.find((line) => {
        const handle = line.merchandise?.product?.handle;
        return handle === productHandle;
      });

      if (productInCart) {
        await cart.remove(productInCart.id, false);
      }
      await addToCart(customKnife.items);
      closeModal();
      handleCloseCustomizer();
      submitKnifeButton.current?.removeAttribute('disabled');
      return;
    }

    await addToCart(customKnife.items);
    closeModal();
    handleCloseCustomizer();
    submitKnifeButton.current?.removeAttribute('disabled');
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleWarrantyModal = async () => {
    const cart = window.GlobalCartContext;
    if (cart?.state?.lines?.length) {
      await cart.remove(
        cart.state.lines.map((line) => line.id),
        false,
      );
    }
    await addToCart(customKnife.items);
    setHasWarranty(false);
    setHasCustomized(false);
    closeModal();
    handleCloseCustomizer();
  };

  const handleNoCustomizationModal = async () => {
    const { mainProduct } = getAllVariants();

    const lineItems = getLineItemAttributes({
      data: mainProduct,
      isLineItem: false,
    });

    const updatedItem = customKnife.items.map((item) => ({
      id: item.id,
      quantity: item.quantity,
      attributes: [...lineItems, { key: '_is_simple', value: 'true' }],
    }));

    await addToCart(updatedItem);
    setHasWarranty(false);
    setHasCustomized(false);
    closeModal();
    handleCloseCustomizer();
  };

  useEffect(() => {
    if (modalOpen && !customKnife) {
      getKnifeData();
    } else if (!modalOpen && customKnife) {
      setCustomKnife();
    }

    if (customKnife && isPersonalizeProduct) {
      handleContinuetoCart();
    }

    if (!modalOpen) {
      setAcknowledgedTerms(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    modalOpen,
    getKnifeDataForCart,
    customKnife,
    getKnifeData,
    isPersonalizeProduct,
  ]);

  const closeTriggerEl = useRef(null);

  const handleSetIsOpen = (isOpen) => {
    if (!isOpen) {
      closeModal();
    }
  };

  const warningModal = useMemo(
    () => (
      <>
        {showWarning && (
          <CartClearWarningModal
            context={hasWarranty ? 'Warranty' : 'Customizer'}
            onCancel={
              hasWarranty
                ? () => {
                    setHasWarranty(false);
                    handleSetIsOpen(false);
                  }
                : () => {
                    setHasCustomized(false);
                    handleSetIsOpen(false);
                  }
            }
            isOpen={showWarning}
            setIsOpen={
              hasWarranty
                ? () => {
                    setHasWarranty(false);
                    handleSetIsOpen(false);
                  }
                : () => {
                    setHasCustomized(false);
                    handleSetIsOpen(false);
                  }
            }
            onContinue={
              hasWarranty ? handleWarrantyModal : handleNoCustomizationModal
            }
            warningText={warningText}
            warningTitle={warningTitle}
          />
        )}
      </>
    ),
    [
      hasWarranty,
      showWarning,
      handleWarrantyModal,
      warningText,
      warningTitle,
      handleSetIsOpen,
    ],
  );

  const reviewTermsText = () => {
    if (isServiceProduct && !window.isSoldOnSite) {
      return reviewTermsNotSoldOnSite;
    }

    if (isServiceProduct && window.isSoldOnSite) {
      return reviewTermsSoldOnSite;
    }

    return reviewTerms;
  };

  if (isPersonalizeProduct && !showWarning) {
    return null;
  }

  if (error) {
    return (
      <ErrorModal>{window.TMW.Strings.customizerError.errorMessage}</ErrorModal>
    );
  }

  // eslint-disable-next-line no-nested-ternary
  return !customKnife ? null : showWarning ? (
    warningModal
  ) : (
    <Panel
      triggers={{
        closeTriggerEl: closeTriggerEl?.current,
      }}
      context="reviewknife"
      classes={'h-full w-[304px] tablet:w-[375px]'}
      showBackdrop={true}
      isOpen={modalOpen}
      setIsOpen={handleSetIsOpen}
      defaultOpen={null}
    >
      <div className="flex flex-col h-full">
        <div className="flex justify-between py-[22px] pr-6 pl-[18px] border-b border-grey-300 laptop:py-[27px] laptop:px-0 laptop:mr-6 laptop:ml-5">
          <h4 className="heading-5">{window.TMW.Strings.reviewKnife.title}</h4>
          <button type="button" ref={closeTriggerEl} onClick={closeModal}>
            <ThreekitIcons type="exitIcon" />
          </button>
        </div>
        <div className="mt-[20px] mb-[37px] px-[20px]">
          <img
            src={customKnife.imageSrc}
            alt="Custom knife"
            className={clsx(
              'w-full',
              productTag === 'cstm:4000' ? 'object-none' : 'object-cover',
            )}
          />
          {customKnife.imageSrcBack && (
            <img
              src={customKnife?.imageSrcBack}
              alt="Custom knife Back"
              className={clsx(
                'w-full',
                productTag === 'cstm:4000' ? 'object-none' : 'object-cover',
              )}
            />
          )}
        </div>
        <div className="overflow-y-scroll grow mx-[20px] p-[20px] bg-bg-6">
          <div className="flex flex-col gap-[23px]">
            {Object.entries(customKnife.categorizedKnifeConfiguration).map(
              ([category, attributes]) => (
                <div
                  className="flex flex-col gap-[15px]"
                  key={`${category}-${attributes[0].value}`}
                >
                  <h7 className="uppercase font-m-12">{category}</h7>
                  <div className="flex flex-col gap-[12px]">
                    {attributes.map(
                      ({ value, attributeName, price }) =>
                        value && (
                          <div
                            className="flex items-center justify-between"
                            key={`${category}-${attributeName}-${value}`}
                          >
                            <p className="font-r-12">
                              {isServiceProduct
                                ? `${attributeName} - ${value}`
                                : value}
                            </p>
                            <p
                              className={clsx(
                                'font-r-12',
                                price > 0
                                  ? 'text-accent-1'
                                  : 'text-secondary-1',
                              )}
                            >
                              +{formatMoney({ amount: price || 0 })}
                            </p>
                          </div>
                        ),
                    )}
                  </div>
                </div>
              ),
            )}
          </div>
        </div>
        <div className="overflow-y-scroll max-h-[130px] p-[20px] m-[20px] bg-bg-6">
          <h7 className="uppercase font-m-12 mb-[10px] block">
            Please reviews our terms
          </h7>
          <div className="font-r-10 flex flex-col gap-[25px]">
            {reviewTermsText()
              .split('\n')
              .filter(Boolean)
              .map((term) => (
                <p key={term}>{term}</p>
              ))}
          </div>
        </div>
        <div className="bg-bg-6 pb-5 w-full pt-2.5">
          <div className="px-5">
            <div className="flex justify-center items-center w-full mb-2.5 pt-[13px] laptop:mb-4 laptop:border-0">
              <input
                type="checkbox"
                className="mr-2"
                id="terms"
                name="terms"
                value={acknowledgedTerms}
                checked={acknowledgedTerms}
                onChange={() => setAcknowledgedTerms(!acknowledgedTerms)}
              />
              <label htmlFor="terms" className="font-r-12">
                {window.TMW.Strings.reviewKnife.acknowledgeTerms}
              </label>
            </div>
            <button
              className={clsx(
                'btn btn-primary-fill w-full h-10 p-0 laptop:h-[50px]',
                true &&
                  'disabled:bg-grey-500 disabled:border-grey-500 disabled:text-white',
              )}
              onClick={handleContinuetoCart}
              disabled={!acknowledgedTerms}
              ref={submitKnifeButton}
            >
              {window.TMW.Strings.reviewKnife.continueToCart}
            </button>
          </div>
        </div>
      </div>
    </Panel>
  );
}

export default ReviewYourKnifeModal;
